import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { PermissionCode } from "@/constants/permissions";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { Flex, HStack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CounterDeliveryEntity,
  CounterDeliveryListResponse,
} from "./schema/counter-delivery.schema";
import { GetCounterDeliveriesProps } from "@/services/counter-delivery.service";
import {
  clearFiltersHandler,
  fetchCounterDeliveriesHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { CounterDeliveryActions } from "@/components/common/section-components/action/CounterDeliveryActions";
import { CounterDeliveryFilter } from "@/components/common/section-components/filters/CounterDeliveryFilter";
import dayjs from "dayjs";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { isEmpty } from "@/utils/query-string";
import { NoData } from "@/components/common/section-components/NoData";
import { StandardTable } from "@/components/table/StandardTable";
import { formatCurrency } from "@/utils/format-currency";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { CounterDeliveryStatus } from "@/constants/entity-status";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { counterDeliveryHeaderElements } from "./schema/header-elements";
import { PendingMessagesAlert } from "@/components/common/section-components/alert/PendingMessagesAlert";
import { usePolling } from "@/hooks/usePolling";

export const CounterDeliveryPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);
  const { userPermissions, fetchPermissions } = useRoles();

  const [loadingCounterDeliveries, setLoadingCounterDeliveries] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const handleAdd = () => {
    navigate(AppRoutes.GRAIN_DELIVERY_CREATION);
  };

  const [counterDeliveries, setCounterDeliveries] = useState<
    CounterDeliveryListResponse | null | undefined
  >();

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const [currentFilters, setCurrentFilters] =
    useState<Partial<GetCounterDeliveriesProps>>();

  const fetchCounterDeliveries = async (
    filters?: GetCounterDeliveriesProps,
    showLoad = true,
  ) => {
    await fetchCounterDeliveriesHandler({
      fetchPermissions,
      navigate,
      setCounterDeliveries,
      setLoadingCounterDeliveries,
      filters,
      showLoad,
    });
  };

  useEffect(() => {
    if (isActive) {
      fetchCounterDeliveries();
    }
  }, [isActive]);

  const { control, handleSubmit, setValue, getValues } = useForm();
  const [page, setPage] = useState(1);
  const [selection, setSelection] = useState<string[]>([]);
  const hasSelection = selection.length > 0;
  const indeterminate = hasSelection && selection.length < selection?.length;

  const submitFilters = async (event: FieldValues) => {
    setPage(1);
    submitFiltersHandler({
      event,
      fetchCounterDeliveries,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
    });
  };

  const clearFilters = () => {
    setPage(1);
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setOpenFilters,
      setValue,
      submitFilters,
    });
  };

  const handlePageChange = (page: number) => {
    setPage(page);
    fetchCounterDeliveries({ ...currentFilters, "P.Pagina": page });
  };

  const handleDetail = (id: any) => {
    navigate(AppRoutes.GRAIN_DELIVERY_DETAIL.replace(":id", String(id)), {
      state: { page },
    });
  };

  const getActionElements = (item: any) => (
    <CounterDeliveryActions handleDetail={handleDetail} item={item} />
  );

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.COUNTER_DELIVERY && p.tipoPermiso === "W",
    ),
  );

  const hasReadAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.COUNTER_DELIVERY && p.tipoPermiso === "R",
    ),
  );

  const values = useWatch({
    control,
  });

  const filtersEmpty = Object.values(values ?? {}).every((v) => isEmpty(v));

    usePolling({
      callback: async () => {
        await fetchCounterDeliveries({ ...currentFilters, "P.Pagina": page }, false);
        if (hasReadAccess) await fetchPermissions(false);
      },
      delay: 10000,
      isActive: isActive && !loadingCounterDeliveries,
    });
  

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        showButtons={hasWriteAccess}
        title="Venta de granos a entregar"
        loading={loadingCounterDeliveries}
        addButtonLabel="Agregar"
        handleAdd={handleAdd}
      />

      <HStack>
        {counterDeliveries?.pendingRequests && !loadingCounterDeliveries && (
          <PendingMessagesAlert
            submitFilters={submitFilters}
            setValue={setValue}
            label="Tiene entregas pendientes de negociación"
            status={CounterDeliveryStatus.IN_NEGOTIATION}
          />
        )}
        <CounterDeliveryFilter
          clearFilters={clearFilters}
          control={control}
          handleSubmit={handleSubmit}
          openFilters={openFilters}
          setOpenFilters={setOpenFilters}
          submitFilters={submitFilters}
          filtersApplied={filtersApplied}
          disableApply={filtersEmpty}
          maxDate={dayjs()}
          disableFilters={loadingCounterDeliveries}
        />
      </HStack>

      <Flex flexDir="column">
        {Boolean(
          !loadingCounterDeliveries && !counterDeliveries?.listado?.length,
        ) && <NoData />}

        {Boolean(
          !loadingCounterDeliveries && counterDeliveries?.listado?.length,
        ) && (
          <>
            <StandardTable
              selection={selection}
              setSelection={setSelection}
              indeterminate={indeterminate}
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                counterDeliveries?.listado?.map(
                  (el: CounterDeliveryEntity) => ({
                    ...el,
                    fechaCreacion: dayjs(el?.fechaCreacion).format(
                      "DD/MM/YYYY",
                    ),
                    monto: el?.sinBase
                      ? "Sin base"
                      : `$${formatCurrency(el?.monto)}`,
                    toneladas: `${el?.toneladas?.toLocaleString("es-AR")}t`,
                    displayEstado: (
                      <RequestStatusBadge
                        status={el?.estadoDescripcion as CounterDeliveryStatus}
                      />
                    ),
                  }),
                ) ?? []
              }
              headers={counterDeliveryHeaderElements}
              actionElements={getActionElements}
            />
            {Number(counterDeliveries?.totalPages) > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={counterDeliveries?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
